import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Eye,
  EyeOff,
  Shield,
  User,
  Building,
  MapPin,
  Award,
  Save,
  Lock,
  Globe,
  Heart,
  Star,
  Camera,
  Upload,
  Trash2,
  X
} from 'lucide-react';
import { PublicProfile } from '../../types/community';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface ProfileManagerProps {
  mode?: 'public' | 'privacy';
}

export const ProfileManager: React.FC<ProfileManagerProps> = ({ mode = 'public' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<Partial<PublicProfile>>({
    isPublic: false,
    showRealName: true,
    showAgency: false,
    showRegion: false,
    bio: '',
    badges: []
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const availableBadges = [
    'First Steps', 'Week Warrior', 'Quick Learner', 'Wellness Master',
    'Community Builder', 'Helpful Hero', 'Stress Buster', 'Night Owl',
    'Team Player', 'Mentor', 'Resilience Champion', 'Mindfulness Master'
  ];

  useEffect(() => {
    if (!user) return;
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      const profileDoc = await getDoc(doc(db, 'publicProfiles', user.uid));
      if (profileDoc.exists()) {
        const profileData = {
          ...profileDoc.data(),
          joinedDate: profileDoc.data().joinedDate?.toDate(),
          createdAt: profileDoc.data().createdAt?.toDate(),
          updatedAt: profileDoc.data().updatedAt?.toDate(),
        } as PublicProfile;
        setProfile(profileData);
      } else {
        // Initialize with user data
        setProfile({
          uid: user.uid,
          displayName: user.displayName || '',
          responderType: user.responderType || 'Other',
          agency: user.agency,
          region: '',
          isPublic: false,
          showRealName: true,
          showAgency: false,
          showRegion: false,
          bio: '',
          badges: []
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!user) return;

    setSaving(true);
    setUploading(!!selectedFile);
    
    try {
      let profilePictureUrl = profile.profilePicture;
      
      // Upload new profile picture if selected
      if (selectedFile) {
        profilePictureUrl = await uploadProfilePicture(selectedFile);
        
        // Delete old profile picture if it exists
        if (profile.profilePicture && profile.profilePicture !== profilePictureUrl) {
          await deleteOldProfilePicture(profile.profilePicture);
        }
      }

      const profileData: Omit<PublicProfile, 'uid'> = {
        displayName: user.displayName || '',
        responderType: user.responderType || 'Other',
        agency: user.agency,
        region: profile.region || '',
        isPublic: profile.isPublic || false,
        showRealName: profile.showRealName || false,
        showAgency: profile.showAgency || false,
        showRegion: profile.showRegion || false,
        bio: profile.bio || '',
        badges: profile.badges || [],
        profilePicture: profilePictureUrl,
        joinedDate: profile.joinedDate || new Date(),
        totalCheckIns: profile.totalCheckIns || 0,
        modulesCompleted: profile.modulesCompleted || 0,
        helpfulPostsCount: profile.helpfulPostsCount || 0,
        createdAt: profile.createdAt || new Date(),
        updatedAt: new Date()
      };

      await setDoc(doc(db, 'publicProfiles', user.uid), profileData);
      
      // Update local profile state
      setProfile({ ...profile, profilePicture: profilePictureUrl });
      setSelectedFile(null);
      setImagePreview(null);
      
      // Trigger profile picture refresh in other components
      window.dispatchEvent(new CustomEvent('profilePictureUpdated', {
        detail: { profilePicture: profilePictureUrl }
      }));
      
      alert('Profile updated successfully!');
      
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const toggleBadge = (badge: string) => {
    const currentBadges = profile.badges || [];
    const newBadges = currentBadges.includes(badge)
      ? currentBadges.filter(b => b !== badge)
      : [...currentBadges, badge];
    
    setProfile({ ...profile, badges: newBadges });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file.');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB.');
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
  };

  const uploadProfilePicture = async (file: File): Promise<string> => {
    if (!user) throw new Error('User not authenticated');
    
    const timestamp = Date.now();
    const fileName = `profile-picture-${timestamp}.${file.name.split('.').pop()}`;
    const storagePath = `users/${user.uid}/${fileName}`;
    const storageRef = ref(storage, storagePath);
    
    console.log('📤 Uploading profile picture:', {
      fileName,
      storagePath,
      fileSize: file.size,
      fileType: file.type,
      userId: user.uid
    });
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    console.log('✅ Profile picture uploaded successfully:', downloadURL);
    return downloadURL;
  };

  const deleteOldProfilePicture = async (url: string) => {
    try {
      const oldRef = ref(storage, url);
      await deleteObject(oldRef);
    } catch (error) {
      console.warn('Could not delete old profile picture:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-2xl animate-float mb-6">
            <User className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Loading Profile</h2>
          <p className="text-gray-600">Setting up your privacy preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/community')}
            className="p-3 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-white transition-all duration-200"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Profile & Privacy</h1>
            <p className="text-gray-600 mt-1">
              Control what information you share with the community
            </p>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
          <div className="flex items-start space-x-4">
            <div className="bg-green-500 p-3 rounded-full">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-green-900 mb-2">Your Privacy is Protected</h3>
              <p className="text-green-800 mb-4">
                Your journal entries, wellness data, and personal information are always private. 
                This page only controls what basic information you want to share with the community.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-green-700">
                <div className="flex items-center space-x-2">
                  <Lock className="w-4 h-4" />
                  <span>Journals always private</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Lock className="w-4 h-4" />
                  <span>Wellness data never shared</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Eye className="w-4 h-4" />
                  <span>You control all public info</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4" />
                  <span>Anonymous options available</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Public Profile Toggle */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Public Profile</h2>
              <p className="text-gray-600 mt-1">
                Allow other first responders to discover and connect with you
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={profile.isPublic}
                onChange={(e) => setProfile({...profile, isPublic: e.target.checked})}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {profile.isPublic ? (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Globe className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">Profile is Public</span>
              </div>
              <p className="text-blue-800 text-sm">
                Other first responders can discover your profile and see the information you choose to share below.
              </p>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <EyeOff className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-900">Profile is Private</span>
              </div>
              <p className="text-gray-700 text-sm">
                Your profile is not discoverable. You can still join groups and participate in discussions.
              </p>
            </div>
          )}
        </div>

        {/* Profile Information */}
        {profile.isPublic && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Public Information</h2>
            
            <div className="space-y-6">
              {/* Name Settings */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center space-x-3">
                  <User className="w-5 h-5 text-gray-600" />
                  <div>
                    <div className="font-medium text-gray-900">Display Name</div>
                    <div className="text-sm text-gray-600">
                      {profile.showRealName ? user?.displayName || 'Your Name' : 'Anonymous Hero'}
                    </div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profile.showRealName}
                    onChange={(e) => setProfile({...profile, showRealName: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Agency Settings */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center space-x-3">
                  <Building className="w-5 h-5 text-gray-600" />
                  <div>
                    <div className="font-medium text-gray-900">Agency/Department</div>
                    <div className="text-sm text-gray-600">
                      {profile.showAgency ? user?.agency || 'Your Department' : 'Hidden'}
                    </div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profile.showAgency}
                    onChange={(e) => setProfile({...profile, showAgency: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Region Settings */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-gray-600" />
                  <div>
                    <div className="font-medium text-gray-900">Region</div>
                    <div className="text-sm text-gray-600">
                      {profile.showRegion ? profile.region || 'Your Region' : 'Hidden'}
                    </div>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={profile.showRegion}
                    onChange={(e) => setProfile({...profile, showRegion: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Region Input */}
              {profile.showRegion && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Region/State
                  </label>
                  <input
                    type="text"
                    value={profile.region || ''}
                    onChange={(e) => setProfile({...profile, region: e.target.value})}
                    placeholder="e.g., California, USA"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
                  />
                </div>
              )}

              {/* Profile Picture */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Picture
                </label>
                <div className="space-y-4">
                  {/* Current/Preview Image */}
                  {(imagePreview || profile.profilePicture) && (
                    <div className="relative inline-block">
                      <img
                        src={imagePreview || profile.profilePicture || ''}
                        alt="Profile preview"
                        className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                      />
                      {imagePreview && (
                        <button
                          onClick={removeImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div>
                    <label className="block">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <span className="text-xs text-gray-500">Upload a profile picture (max 5MB)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio (Optional)
                </label>
                <textarea
                  value={profile.bio || ''}
                  onChange={(e) => setProfile({...profile, bio: e.target.value})}
                  placeholder="Share a bit about yourself and your experience as a first responder..."
                  rows={4}
                  maxLength={300}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 resize-none"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {(profile.bio || '').length}/300 characters
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Achievement Badges */}
        {profile.isPublic && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Achievement Badges</h2>
            <p className="text-gray-600 mb-6">
              Choose which achievements you'd like to display on your public profile
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {availableBadges.map((badge) => {
                const isSelected = profile.badges?.includes(badge);
                
                return (
                  <button
                    key={badge}
                    onClick={() => toggleBadge(badge)}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 text-center ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${
                      isSelected ? 'bg-blue-500' : 'bg-gray-200'
                    }`}>
                      <Award className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                      {badge}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Profile Preview */}
        {profile.isPublic && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Profile Preview</h2>
            <div className="border border-gray-200 rounded-xl p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white text-xl font-bold">
                    {(profile.showRealName ? user?.displayName : 'Anonymous')?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {profile.showRealName ? user?.displayName : 'Anonymous Hero'}
                  </h3>
                  <p className="text-gray-600">{user?.responderType}</p>
                  {profile.showAgency && user?.agency && (
                    <p className="text-sm text-gray-500">{user.agency}</p>
                  )}
                  {profile.showRegion && profile.region && (
                    <p className="text-sm text-gray-500">{profile.region}</p>
                  )}
                </div>
              </div>

              {profile.bio && (
                <p className="text-gray-700 mb-4">{profile.bio}</p>
              )}

              {profile.badges && profile.badges.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Achievements</h4>
                  <div className="flex flex-wrap gap-2">
                    {profile.badges.map((badge, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                      >
                        <Star className="w-3 h-3 mr-1" />
                        {badge}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/community')}
            className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Community</span>
          </button>

          <button
            onClick={saveProfile}
            disabled={saving}
            className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" color="white" />
                <span>{uploading ? 'Uploading...' : 'Saving...'}</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Save Profile</span>
              </>
            )}
          </button>
        </div>

        {/* Privacy Reminder */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-yellow-600 mt-1" />
            <div>
              <h3 className="font-medium text-yellow-900 mb-1">Privacy Reminder</h3>
              <p className="text-yellow-800 text-sm">
                Remember: Your wellness journals, mood data, and personal health information are never shared 
                and remain completely private regardless of these settings. This only controls basic profile 
                information for community connections.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
